import { useCallback, useEffect, useState } from 'react'
import store from 'store'

// Ideally, we would use usehooks-ts, but they have an edge-case that prevents us from using their solution:
// https://github.com/juliencrn/usehooks-ts/issues/549

declare global {
  interface WindowEventMap {
    'local-storage': StorageEvent
  }
}

/**
 * Rough idea adopted from https://usehooks.com/useLocalStorage/ and modified
 * for our own needs.
 *
 * Usage: as with `useState`, but persists to local storage.
 * Syncs across components sharing the same key and across browser tabs.
 */
export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const storedInitialValue = store.get(key)
      if (storedInitialValue === undefined) {
        store.set(key, initialValue)
        return initialValue
      } else {
        return storedInitialValue
      }
    } catch (error) {
      console.error(
        `Couldn't retrieve initialValue from localStore for "${key}"`,
        error,
      )
      return initialValue
    }
  })

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Compute valueToStore outside of setStoredValue to avoid
        // side effects inside the state updater (required for React 18+ concurrent mode)
        const valueToStore =
          value instanceof Function ? value(storedValue) : value
        setStoredValue(valueToStore)
        store.set(key, valueToStore)

        // Notify other components in the same window
        window.dispatchEvent(new StorageEvent('local-storage', { key }))
      } catch (error) {
        console.error(`Couldn't save value to localStore for "${key}"`, error)
        throw error
      }
    },
    [key, storedValue],
  )

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && event.key !== key) {
        return
      }
      setStoredValue(store.get(key))
    }

    // Sync across browser tabs/windows (native storage event)
    window.addEventListener('storage', handleStorageChange)
    // Sync across components in the same window (custom event)
    window.addEventListener('local-storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('local-storage', handleStorageChange)
    }
  }, [key])

  return [storedValue, setValue]
}
